import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Radio, Battery, Droplets, Power, RefreshCw, Activity, AlertTriangle,
  CheckCircle2, Clock, XCircle, Cpu, Waves, FileText, FileDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useIntlFormatting } from '../utils/intl';
import { useAuth } from '../contexts/AuthContext';
import { canIssuePhysicalCommands } from '../auth/cognito';
import { iotApi, IoTNode, Command, IoTAlert, DashboardStats, TelemetrySummary, NodeLatest, ApiError } from '../lib/iotApi';
import {
  ACTION_BY_STATE,
  ASVWF01_CHANNELS,
  VALVE_STATE_MODELS,
  channelTargetKey,
  commandStatusKind,
  generateIdempotencyKey,
  isBusyStatus,
  isRefreshProven,
  isTerminalCommandStatus,
  nodeTimestampMs,
  parseTimestampMs,
  resolveScalarValveState,
  scalarTargetKey,
  targetKey,
  valveCommandPath,
  valveTargetsFor,
  type RefreshProof,
  type ValveTarget,
} from '../lib/valveCommandSafety';
import TelemetryChart, { TelemetryPoint } from '../components/TelemetryChart';
import ReportSchedules from '../components/ReportSchedules';
import {
  IS_AWS_MODE,
  isTelemetryEnabled,
  isTtnSimulatorEnabled,
  supportsValveControl,
} from '../config/frontend';

const TELEMETRY_ENABLED = isTelemetryEnabled();
const TTN_SIMULATOR_ENABLED = isTtnSimulatorEnabled();

const PERIODS: { key: number; label: string }[] = [
  { key: 6, label: '6 h' },
  { key: 24, label: '24 h' },
  { key: 168, label: '7 j' },
];

const fmtMinutes = (min: number): string => {
  if (!min) return '0 min';
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m} min`;
};

// ---------------------------------------------------------------------------
// FIELD-UI-B4 — farmer read-only Pilotage data derivation (AWS mode only)
// ---------------------------------------------------------------------------

const UNKNOWN = '—';

type MeasureKey = keyof NonNullable<NodeLatest['measures']>;

type StateKind = 'valve' | 'pump' | 'motor';

// relay_state is intentionally excluded — a relay channel must never imply a pump/motor/valve.
const STATE_ALIASES: { key: MeasureKey; kind: StateKind }[] = [
  { key: 'valve_state', kind: 'valve' },
  { key: 'pump_state', kind: 'pump' },
  { key: 'motor_state', kind: 'motor' },
];

// valve_state is a meaningful semantic state only for valve-capable models
// (VALVE_STATE_MODELS, shared). For passive sensors, relays, and unknown models
// a compatibility valve_state 0/1 MUST NOT render a valve state (it renders —).

/** Explicit semantic equipment state (0/1) only; absent/unknown → null. */
const selectState = (node: IoTNode): { kind: StateKind; value: number } | null => {
  const measures = node.latest?.measures;

  // Valve state (valve models only): resolve freshness between root and latest.
  const freshValve = resolveScalarValveState(node);
  if (freshValve !== null) {
    return { kind: 'valve', value: freshValve };
  }

  // pump_state / motor_state — explicit semantic states, any model.
  if (measures) {
    for (const entry of STATE_ALIASES) {
      if (entry.kind === 'valve') continue;
      const value = measures[entry.key];
      if (value === 0 || value === 1) return { kind: entry.kind, value };
    }
  }

  return null;
};

/** Friendly display text; missing/blank → UNKNOWN (never a fake value). */
const displayText = (raw: string | null | undefined): string =>
  typeof raw === 'string' && raw.trim().length > 0 ? raw : UNKNOWN;

// ---------------------------------------------------------------------------
// FIELD-UI-CONTROL — safe farmer valve controls (AWS mode only)
// ---------------------------------------------------------------------------

// Physical-action authorization is delegated to the shared #103 primitive
// canIssuePhysicalCommands (frontend/src/auth/cognito.ts). It is consumed for
// BOTH render/enable gating and the submit-time recheck in confirmCommand;
// the backend remains authoritative for membership / FarmGrant / node authz.

// The ASVWF01 channel list, busy/terminal status vocabulary, target keys,
// per-channel canonical state derivation, target-state/snapshot readers,
// refresh-proof shape and idempotency-key generator are the SHARED primitives
// in lib/valveCommandSafety (imported above). They are the single definition
// reused by the operational Sector Detail — no simplified parallel copy exists.

// ---------------------------------------------------------------------------
// FIELD-UI-MULTICLIENT-0001 — semantic pump controls (PR #105)
// ---------------------------------------------------------------------------
//
// A pump UI candidate requires pump_control to be an object with a non-empty
// pump_id. Command enablement additionally requires COMMISSIONED; anything else
// fails closed visually. The frontend never renders internal relay/channel/
// hydraulic fields. A relay command is never presented as proof of motor state.

// Strict pump command status contract (PR #105 / #106).
// Recognized backend statuses are exactly these five; the UI-local UNCERTAIN
// is the only additional safety state. Anything else normalizes to UNCERTAIN
// so an unknown/missing/malformed status can never re-enable the controls.
type PumpCommandStatus =
  | 'REQUESTED'
  | 'SENT_TO_TTN'
  | 'OBSERVED_SUCCESS'
  | 'OBSERVED_FAILED'
  | 'TIMEOUT'
  | 'UNCERTAIN';

const PUMP_RECOGNIZED_STATUSES: ReadonlySet<string> = new Set([
  'REQUESTED',
  'SENT_TO_TTN',
  'OBSERVED_SUCCESS',
  'OBSERVED_FAILED',
  'TIMEOUT',
]);

const PUMP_TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  'OBSERVED_SUCCESS',
  'OBSERVED_FAILED',
  'TIMEOUT',
]);

/**
 * Normalize an arbitrary backend status string into the strict contract.
 * Only the exact five recognized statuses pass through; anything else
 * (unknown, missing, empty, wrong type) becomes UNCERTAIN.
 */
const normalizePumpBackendStatus = (raw: unknown): PumpCommandStatus =>
  typeof raw === 'string' && PUMP_RECOGNIZED_STATUSES.has(raw)
    ? (raw as PumpCommandStatus)
    : 'UNCERTAIN';

const isPumpTerminalStatus = (s: string | null | undefined): boolean =>
  !!s && PUMP_TERMINAL_STATUSES.has(s);

const isPumpBusyStatus = (raw: string | null | undefined): boolean =>
  !!raw && !isPumpTerminalStatus(raw);

interface PumpBinding {
  nodeId: string;
  nodeName: string;
  pumpId: string;
  commissioned: boolean;
}

const pumpBindingsFor = (nodes: IoTNode[]): PumpBinding[] => {
  const out: PumpBinding[] = [];
  for (const node of nodes) {
    const pc = node.pump_control;
    if (!pc || typeof pc !== 'object') continue;
    const pumpId = typeof pc.pump_id === 'string' ? pc.pump_id.trim() : '';
    if (pumpId.length === 0) continue;
    out.push({
      nodeId: node.id,
      nodeName: displayText(node.name),
      pumpId,
      commissioned: pc.status === 'COMMISSIONED',
    });
  }
  return out;
};

// One stable unique key per confirmed pump intent. Never reused across intents;
// never regenerated for the same intent (no automatic retry).
let pumpIdempotencySeq = 0;
const generatePumpIdempotencyKey = (): string => {
  pumpIdempotencySeq += 1;
  const rand = Math.random().toString(36).slice(2, 10);
  return `pump-${Date.now().toString(36)}-${pumpIdempotencySeq.toString(36)}-${rand}`;
};

interface PendingPumpConfirmation {
  pumpId: string;
  nodeId: string;
  action: 'start' | 'stop';
  equipment: string;
  idempotencyKey: string;
}

interface PendingConfirmation {
  target: ValveTarget;
  action: 'on' | 'off';
  equipment: string;
  sector: string | null;
  channelLabel: string | null;
  idempotencyKey: string;
}

interface IoTNodesProps {
  farmId?: string;
  allowPumpControl?: boolean;
}

const IoTNodes: React.FC<IoTNodesProps> = ({ farmId, allowPumpControl }) => {
  const { t } = useTranslation('iot');
  const { formatDateTime } = useIntlFormatting();
  const { user } = useAuth();
  // Canonical node-list read path. A farm-scoped caller must never issue an
  // unqualified /iot/nodes read; the generic (non-CGM) component keeps its
  // exact historical behavior with the bare /iot/nodes route ONLY when no
  // farmId was provided at all. A provided-but-empty farmId still builds
  // `/iot/nodes?farm_id=` so the backend fail-closes rather than silently
  // widening to an unfiltered read.
  const nodeListPath =
    farmId === undefined
      ? '/iot/nodes'
      : `/iot/nodes?farm_id=${encodeURIComponent(farmId)}`;
  const canControl = canIssuePhysicalCommands(user?.role);
  // Topology pumping capability gate: only an explicit `false` disables the pump
  // surface. `undefined` (generic/historical callers) and `true` both preserve
  // the full semantic pump controls. The server capability is the authority.
  const pumpsEnabled = allowPumpControl !== false;
  const [nodes, setNodes] = useState<IoTNode[]>([]);
  const [commands, setCommands] = useState<Command[]>([]);
  const [alerts, setAlerts] = useState<IoTAlert[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [telemetry, setTelemetry] = useState<Record<string, TelemetryPoint[]>>({});
  const [summaries, setSummaries] = useState<Record<string, TelemetrySummary>>({});
  const [period, setPeriod] = useState<number>(24);
  const [busy, setBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [farmerNodes, setFarmerNodes] = useState<IoTNode[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  const [commandStatus, setCommandStatus] = useState<Record<string, string>>({});
  const [commandsLoaded, setCommandsLoaded] = useState<Record<string, boolean>>({});
  const [activeCommandId, setActiveCommandId] = useState<Record<string, string>>({});
  const valveNodeIdsRef = useRef<string[]>([]);
  const commandStatusRef = useRef<Record<string, string>>({});
  const activeCommandIdRef = useRef<Record<string, string>>({});
  const commandsLoadedRef = useRef<Record<string, boolean>>({});
  const commandsInFlightRef = useRef(false);
  // Generation-aware post-success refresh causality: each target that enters
  // REFRESHING is stamped with a monotonic generation; a successful refresh may
  // clear only targets whose generation is <= the generation captured when that
  // refresh started (so a refresh can never clear a newer success).
  const refreshingGenerationRef = useRef<Record<string, number>>({});
  const refreshGenerationRef = useRef(0);
  // Per-target refresh proof (expected-state or observation-timestamp).  A
  // REFRESHING target may clear only when this proof is satisfied by the
  // refreshed node snapshot, in addition to the generation guard.
  const refreshProofRef = useRef<Record<string, RefreshProof>>({});
  const mountedRef = useRef(true);
  const nodesInFlightRef = useRef(false);
  const refreshQueuedRef = useRef(false);
  // Pump command lifecycle state (keyed by pump_id). The action is retained so
  // the exact semantic status maps to start/stop-specific farmer language.
  const [pumpStatus, setPumpStatus] = useState<Record<string, string>>({});
  const [pumpAction, setPumpAction] = useState<Record<string, 'start' | 'stop'>>({});
  const pumpStatusRef = useRef<Record<string, string>>({});
  const pumpActionRef = useRef<Record<string, 'start' | 'stop'>>({});
  const pumpCommandIdRef = useRef<Record<string, string>>({});
  const pumpNodeIdRef = useRef<Record<string, string>>({});
  const pumpPollInFlightRef = useRef(false);
  const [pendingPumpConfirmation, setPendingPumpConfirmation] = useState<PendingPumpConfirmation | null>(null);
  const pendingPumpConfirmationRef = useRef<PendingPumpConfirmation | null>(null);

  const statusLabel = (s: string): string => {
    switch (s) {
      case 'OBSERVED_SUCCESS': return t('status.observed_success');
      case 'SENT_TO_TTN': return t('status.sentToTtn');
      case 'REQUESTED': return t('status.requested');
      case 'TIMEOUT': return t('status.timeout');
      case 'OBSERVED_FAILED': return t('status.observed_failed');
      default: return s;
    }
  };

  const statusStyle = (s: string): { color: string; icon: React.ElementType; label: string } => {
    switch (s) {
      case 'OBSERVED_SUCCESS':
        return { color: 'var(--color-success)', icon: CheckCircle2, label: t('status.observed_success') };
      case 'SENT_TO_TTN':
        return { color: 'var(--color-info)', icon: Radio, label: t('status.sentToTtn') };
      case 'REQUESTED':
        return { color: 'var(--color-warning)', icon: Clock, label: t('status.requested') };
      case 'TIMEOUT':
        return { color: 'var(--color-warning)', icon: Clock, label: t('status.timeout') };
      case 'OBSERVED_FAILED':
        return { color: 'var(--color-danger)', icon: XCircle, label: t('status.observed_failed') };
      default:
        return { color: 'var(--text-secondary)', icon: Clock, label: s };
    }
  };

  const fmtTime = (iso?: string | null) =>
    iso ? formatDateTime(iso) : '—';

  const refresh = useCallback(async () => {
    try {
      const [n, c, a, s] = await Promise.all([
        iotApi.get(nodeListPath),
        iotApi.get('/iot/commands'),
        iotApi.get('/iot/alerts'),
        iotApi.get('/iot/dashboard'),
      ]);
      setNodes(n);
      setCommands(c);
      setAlerts(a);
      setStats(s);
      if (!TELEMETRY_ENABLED) {
        setTelemetry({});
        setSummaries({});
        return;
      }
      // Windowed telemetry + aggregated summary per node
      const nodeList = n as IoTNode[];
      const series = await Promise.all(
        nodeList.map((node) =>
          iotApi.get(`/iot/nodes/${node.id}/telemetry?hours=${period}&limit=2000`)
            .then((rows) => [node.id, rows] as const).catch(() => [node.id, []] as const)
        )
      );
      const sums = await Promise.all(
        nodeList.map((node) =>
          iotApi.get(`/iot/nodes/${node.id}/telemetry/summary?hours=${period}`)
            .then((sum) => [node.id, sum] as const).catch(() => [node.id, null] as const)
        )
      );
      setTelemetry(Object.fromEntries(series));
      setSummaries(Object.fromEntries(sums.filter(([, v]) => v)));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [period, nodeListPath]);

  // -------------------------------------------------------------------------
  // Node refresh: periodic poll + mandatory post-success refresh.
  // A post-success refresh is guaranteed to complete (queued if a GET is already
  // in flight); only a successful refresh begun after the success clears the
  // REFRESHING safety lock (never a stale in-flight poll).
  // -------------------------------------------------------------------------

  // Clear REFRESHING only for targets whose success-generation is <= the given
  // generation (a refresh can never clear a newer success) AND whose refreshed
  // node snapshot proves the successful physical outcome.  Proof is either the
  // refreshed target state matching the locally-known desired state, or the
  // refreshed node timestamp being at/after the successful observation.  A
  // stale / absent / unknown / malformed snapshot keeps the target locked.
  const clearProvenRefreshing = useCallback((generation: number, nodeList: IoTNode[]) => {
    const statusPrev = commandStatusRef.current;
    const genPrev = refreshingGenerationRef.current;
    const proofPrev = refreshProofRef.current;
    const nodesById = new Map(nodeList.map((n) => [n.id, n]));
    const keys = Object.keys(statusPrev).filter(
      (k) =>
        statusPrev[k] === 'REFRESHING' &&
        (genPrev[k] ?? Number.POSITIVE_INFINITY) <= generation,
    );
    if (keys.length === 0) return;
    const nextStatus = { ...statusPrev };
    const nextGen = { ...genPrev };
    const nextProof = { ...proofPrev };
    for (const k of keys) {
      const proof = proofPrev[k];
      if (proof === undefined) continue; // no proof → fail closed (keep REFRESHING)
      // Absent node, stale/unknown snapshot or mismatching state → keep REFRESHING.
      if (!isRefreshProven(proof, nodesById.get(proof.nodeId))) continue;
      delete nextStatus[k];
      delete nextGen[k];
      delete nextProof[k];
    }
    commandStatusRef.current = nextStatus;
    refreshingGenerationRef.current = nextGen;
    refreshProofRef.current = nextProof;
    setCommandStatus(nextStatus);
  }, []);

  const performReloadNodes = useCallback(() => {
    nodesInFlightRef.current = true;
    // Capture the generation at START: this refresh may only clear targets whose
    // success happened at or before this generation (never a newer success).
    const refreshGeneration = refreshGenerationRef.current;
    iotApi
      .getJson<IoTNode[]>(nodeListPath)
      .then((list) => {
        nodesInFlightRef.current = false;
        if (!mountedRef.current) return;
        setFarmerNodes(list);
        setLoadError(false);
        setLoaded(true);
        clearProvenRefreshing(refreshGeneration, list);
        if (refreshQueuedRef.current) {
          refreshQueuedRef.current = false;
          performReloadNodes();
        }
      })
      .catch(() => {
        nodesInFlightRef.current = false;
        if (!mountedRef.current) return;
        setLoadError(true);
        setLoaded(true);
        // Fail closed: keep REFRESHING; a queued refresh is retried on success.
        if (refreshQueuedRef.current) {
          refreshQueuedRef.current = false;
          performReloadNodes();
        }
      });
  }, [clearProvenRefreshing, nodeListPath]);

  const reloadNodes = useCallback(() => {
    if (nodesInFlightRef.current) return;
    performReloadNodes();
  }, [performReloadNodes]);

  const requestPostSuccessRefresh = useCallback(() => {
    if (nodesInFlightRef.current) {
      refreshQueuedRef.current = true;
      return;
    }
    performReloadNodes();
  }, [performReloadNodes]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!IS_AWS_MODE) return;
    reloadNodes();
    const interval = setInterval(reloadNodes, 30000);
    return () => clearInterval(interval);
  }, [reloadNodes]);

  // Set one target's local safety state (status + optional tracked command id).
  const setTargetCommandState = useCallback(
    (key: string, status: string | null, commandId?: string) => {
      const nextStatus = { ...commandStatusRef.current };
      const nextId = { ...activeCommandIdRef.current };
      if (status === null) {
        delete nextStatus[key];
        delete nextId[key];
      } else {
        nextStatus[key] = status;
        if (commandId) nextId[key] = commandId;
        else delete nextId[key];
      }
      commandStatusRef.current = nextStatus;
      activeCommandIdRef.current = nextId;
      setCommandStatus(nextStatus);
      setActiveCommandId(nextId);
    },
    [],
  );

  // Drop the refresh proof for a target (terminal failure, conflict, denial,
  // ambiguous outcome) so a stale expectation can never unlock a later intent.
  const clearRefreshProof = useCallback((key: string) => {
    const nextProof = { ...refreshProofRef.current };
    delete nextProof[key];
    refreshProofRef.current = nextProof;
  }, []);

  // Farmer-safe command-status polling (GET-only, bounded single batch read).
  // Fail-closed: a node's controls stay disabled until its first successful
  // command read; a failed read preserves any prior busy/uncertain state (never
  // "no command"); the exact active command id is followed so an empty/stale/
  // historical list can never clear or terminalize the current intent.
  const pollCommands = useCallback(() => {
    if (commandsInFlightRef.current) return;
    const ids = valveNodeIdsRef.current;
    if (ids.length === 0) return;
    commandsInFlightRef.current = true;

    // ONE bounded batch read per polling cycle (FIELD #103 batch endpoint).
    // node_ids = every displayed valve-capable node; command_ids = every
    // currently tracked active command id (omitted when there are none).
    const nodeIdsParam = ids.map((id) => encodeURIComponent(id)).join(',');
    let query = `node_ids=${nodeIdsParam}`;
    const trackedIds = Object.values(activeCommandIdRef.current).filter(Boolean);
    if (trackedIds.length > 0) {
      query += `&command_ids=${trackedIds
        .map((id) => encodeURIComponent(id))
        .join(',')}`;
    }

    iotApi
      .getJson<Command[]>(`/iot/commands?${query}`)
      .then((raw) => {
        commandsInFlightRef.current = false;
        if (!mountedRef.current) return;

        // Normalize the batch safety view (`command_id`) to the internal `id`
        // the state machine already tracks, then group commands by node locally.
        const list = (Array.isArray(raw) ? (raw as Command[]) : []).map((c) => ({
          ...c,
          id: c.command_id ?? c.id,
        }));
        const byNode = new Map<string, Command[]>();
        for (const c of list) {
          if (typeof c.node_id !== 'string') continue;
          const group = byNode.get(c.node_id) ?? [];
          group.push(c);
          byNode.set(c.node_id, group);
        }

        const nextStatus = { ...commandStatusRef.current };
        const nextId = { ...activeCommandIdRef.current };
        const nextLoaded = { ...commandsLoadedRef.current };
        const nextRefreshingGeneration = { ...refreshingGenerationRef.current };
        const nextProof = { ...refreshProofRef.current };
        let sawTerminalSuccess = false;

        for (const nodeId of ids) {
          // A successful batch read is a successful command read for EVERY
          // requested valve node — even one returning zero commands.
          nextLoaded[nodeId] = true;
          const group = byNode.get(nodeId) ?? [];

          const applyTarget = (key: string, channelId: string | undefined) => {
            const prev = nextStatus[key];
            const trackedId = nextId[key];
            const matches = group.filter(
              (c) =>
                c.type === 'valve_control' &&
                (channelId ? c.channel_id === channelId : !c.channel_id),
            );

            if (trackedId) {
              // Following an exact command id.
              const cmd = matches.find((c) => c.id === trackedId);
              if (!cmd) return; // temporarily absent → preserve busy state
              if (isTerminalCommandStatus(cmd.status)) {
                delete nextId[key];
                if (
                  cmd.status === 'OBSERVED_SUCCESS' &&
                  prev !== undefined &&
                  isBusyStatus(prev) &&
                  prev !== 'REFRESHING'
                ) {
                  nextStatus[key] = 'REFRESHING';
                  refreshGenerationRef.current += 1;
                  nextRefreshingGeneration[key] = refreshGenerationRef.current;
                  // Keep the locally-known expected-state proof when present;
                  // otherwise prove the post-success refresh via the command's
                  // observation timestamp (external / adopted command).
                  if (!nextProof[key] || nextProof[key].kind !== 'expectedState') {
                    const obsTs = parseTimestampMs(cmd.observed_at ?? cmd.terminal_at);
                    if (obsTs !== null) {
                      nextProof[key] = { kind: 'observationTimestamp', nodeId, channelId: channelId ?? null, ts: obsTs };
                    } else {
                      delete nextProof[key]; // no usable proof → fail closed
                    }
                  }
                  sawTerminalSuccess = true;
                } else {
                  nextStatus[key] = cmd.status;
                  delete nextProof[key]; // terminal failure/timeout → drop proof
                }
              } else {
                nextStatus[key] = cmd.status;
                nextId[key] = cmd.id;
              }
              return;
            }

            if (prev !== undefined && isBusyStatus(prev) && prev !== 'REFRESHING') {
              // Local busy without a tracked id (409 REQUESTED / SENT_TO_TTN /
              // UNCERTAIN): adopt the latest matching non-terminal command;
              // otherwise preserve — empty/historical must not unlock.
              const pendingCmd = matches.find(
                (c) => c.status === 'REQUESTED' || c.status === 'SENT_TO_TTN',
              );
              if (pendingCmd) {
                nextStatus[key] = pendingCmd.status;
                nextId[key] = pendingCmd.id;
              }
              return;
            }

            if (prev === 'REFRESHING') return; // awaiting post-success refresh

            // Not busy (terminal or no command): reflect latest backend state.
            const latest = matches[0];
            if (latest) {
              if (isTerminalCommandStatus(latest.status)) {
                delete nextId[key];
                nextStatus[key] = latest.status;
                delete nextProof[key]; // terminal → drop any stale proof
              } else {
                nextStatus[key] = latest.status;
                nextId[key] = latest.id;
              }
            } else if (prev === undefined) {
              // No matching command in the batch: only an initially-unloaded
              // target clears to "no command". A prior terminal status
              // (OBSERVED_FAILED / TIMEOUT) is preserved — the batch read never
              // returns terminal non-tracked commands, so absence must not erase
              // the last resolved outcome.
              delete nextStatus[key];
              delete nextId[key];
            }
          };

          applyTarget(scalarTargetKey(nodeId), undefined);
          for (const ch of ASVWF01_CHANNELS) {
            applyTarget(channelTargetKey(nodeId, ch.id), ch.id);
          }
        }

        commandStatusRef.current = nextStatus;
        activeCommandIdRef.current = nextId;
        commandsLoadedRef.current = nextLoaded;
        refreshingGenerationRef.current = nextRefreshingGeneration;
        refreshProofRef.current = nextProof;
        setCommandStatus(nextStatus);
        setActiveCommandId(nextId);
        setCommandsLoaded(nextLoaded);

        if (sawTerminalSuccess) {
          requestPostSuccessRefresh();
        }
      })
      .catch(() => {
        // Batch read failure (HTTP 503/500, network, CORS, abort, unknown) →
        // fail closed: the UI can no longer prove there is no active command
        // for ANY requested node, so reset their command-read readiness (their
        // controls become disabled) while preserving every prior busy/uncertain
        // status, tracked id and REFRESHING state.  No POST, no TIMEOUT
        // invention, no per-node fallback reads, no retry fan-out.
        commandsInFlightRef.current = false;
        if (!mountedRef.current) return;
        const nextLoaded = { ...commandsLoadedRef.current };
        for (const id of ids) {
          nextLoaded[id] = false;
        }
        commandsLoadedRef.current = nextLoaded;
        setCommandsLoaded(nextLoaded);
      });
  }, [requestPostSuccessRefresh]);

  // Track valve-capable node ids; trigger an immediate safety read the first
  // time they become available.
  useEffect(() => {
    const ids = farmerNodes
      .filter((n) => n.model === 'valve_water_flow' || n.model === 'asvwf01')
      .map((n) => n.id);
    const prevIds = valveNodeIdsRef.current;
    const changed =
      ids.length !== prevIds.length || ids.some((id, i) => id !== prevIds[i]);
    valveNodeIdsRef.current = ids;
    if (changed && ids.length > 0) {
      pollCommands();
    }
  }, [farmerNodes, pollCommands]);

  // Ongoing command-status polling (~30s), never overlapping; GET-only.
  useEffect(() => {
    if (!IS_AWS_MODE) return;
    pollCommands();
    const interval = setInterval(pollCommands, 30000);
    return () => clearInterval(interval);
  }, [pollCommands]);

  // Open the mandatory confirmation dialog (zero POST until confirmed).
  const openConfirm = (
    target: ValveTarget,
    action: 'on' | 'off',
    node: IoTNode,
    channelLabel: string | null,
  ) => {
    setPendingConfirmation({
      target,
      action,
      equipment: displayText(node.name),
      sector: node.sector_name && node.sector_name.trim().length > 0 ? node.sector_name : null,
      channelLabel,
      idempotencyKey: generateIdempotencyKey(),
    });
  };

  const cancelConfirm = () => setPendingConfirmation(null);

  // Invalidate a stale confirmation the moment its exact target becomes busy via
  // command polling (REQUESTED/SENT_TO_TTN/UNCERTAIN/REFRESHING) OR the node
  // loses command-read readiness (polling failed → commandsLoaded false).  Zero
  // POST; the target's existing command status and active command id preserved.
  useEffect(() => {
    if (!pendingConfirmation) return;
    const key = targetKey(pendingConfirmation.target);
    const nodeId = pendingConfirmation.target.nodeId;
    if (isBusyStatus(commandStatus[key]) || commandsLoaded[nodeId] !== true) {
      setPendingConfirmation(null);
    }
  }, [pendingConfirmation, commandStatus, commandsLoaded]);

  // Issue exactly one physical POST for a confirmed intent, carrying a stable
  // Idempotency-Key.  Never auto-retries; failures surface farmer-safe copy only.
  const confirmCommand = async () => {
    if (!pendingConfirmation) return;
    const { target, action, idempotencyKey } = pendingConfirmation;
    const key = targetKey(target);

    // Submit-time fail-closed guards (authoritative ref-level rechecks), BEFORE
    // any confirmation clear, state mutation, or POST.  Role, command-read
    // readiness and target-busy are ALL mandatory.
    if (!canIssuePhysicalCommands(user?.role)) {
      setPendingConfirmation(null);
      toast.error(t('farmer.errors.forbidden'));
      return;
    }
    if (commandsLoadedRef.current[target.nodeId] !== true) {
      // Command-read readiness lost (polling failed) — never POST while command
      // status is unknown.
      setPendingConfirmation(null);
      toast.error(t('farmer.errors.uncertain'));
      return;
    }
    if (isBusyStatus(commandStatusRef.current[key])) {
      setPendingConfirmation(null);
      toast.error(t('farmer.errors.conflict'));
      return;
    }

    setPendingConfirmation(null);
    setTargetCommandState(key, 'REQUESTED');
    // Record the locally-known desired state (closed→open / open→close) as the
    // post-success refresh proof for this exact target.  The backend command was
    // generated only from the opposite known state, so an exact-state match on
    // refresh is valid proof of the physical outcome.
    refreshProofRef.current = {
      ...refreshProofRef.current,
      [key]: {
        kind: 'expectedState',
        nodeId: target.nodeId,
        channelId: target.kind === 'channel' ? target.channelId : null,
        value: action === 'on' ? 1 : 0,
      },
    };
    const path = valveCommandPath(target);
    try {
      const res = await iotApi.post(path, { state: action }, idempotencyKey);
      const status = typeof res?.status === 'string' ? res.status : 'REQUESTED';
      const commandId = typeof res?.command_id === 'string' ? res.command_id : undefined;
      if (status === 'OBSERVED_SUCCESS') {
        // Direct (mock) success → lock + mandatory node refresh.
        setTargetCommandState(key, 'REFRESHING');
        refreshGenerationRef.current += 1;
        refreshingGenerationRef.current = {
          ...refreshingGenerationRef.current,
          [key]: refreshGenerationRef.current,
        };
        requestPostSuccessRefresh();
      } else if (isTerminalCommandStatus(status)) {
        setTargetCommandState(key, status);
        // Terminal failure/timeout: no refresh proof needed — drop it.
        clearRefreshProof(key);
      } else {
        // Non-terminal → track the exact command id (keep the expected-state proof).
        setTargetCommandState(key, status, commandId);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.semanticCode === 'conflict' || err.httpStatus === 409) {
          // A pending command already exists — keep target pending/disabled.
          // Our desired state is NOT proof for another user's command.
          setTargetCommandState(key, 'REQUESTED');
          clearRefreshProof(key);
          toast.error(t('farmer.errors.conflict'));
        } else if (err.semanticCode === 'forbidden' || err.httpStatus === 403) {
          // Definite permission denial — not command-busy.
          setTargetCommandState(key, null);
          clearRefreshProof(key);
          toast.error(t('farmer.errors.forbidden'));
        } else {
          // Ambiguous network/server/unknown — fail closed, do not re-enable.
          // Our command may not have been issued, so the desired state is not
          // a reliable proof for whichever command later appears.
          setTargetCommandState(key, 'UNCERTAIN');
          clearRefreshProof(key);
          toast.error(t('farmer.errors.uncertain'));
        }
      } else {
        setTargetCommandState(key, 'UNCERTAIN');
        clearRefreshProof(key);
        toast.error(t('farmer.errors.uncertain'));
      }
    }
  };

  // -------------------------------------------------------------------------
  // Pump command lifecycle (PR #105 semantic endpoint). Mirrors the valve
  // safety discipline without touching the valve batch polling state machine.
  // -------------------------------------------------------------------------

  // Set one pump's local lifecycle state (status + optional exact command id).
  const setPumpIntent = useCallback(
    (
      pumpId: string,
      status: string | null,
      action?: 'start' | 'stop',
      commandId?: string,
    ) => {
      const nextStatus = { ...pumpStatusRef.current };
      const nextAction = { ...pumpActionRef.current };
      const nextCmd = { ...pumpCommandIdRef.current };
      if (status === null) {
        delete nextStatus[pumpId];
        delete nextAction[pumpId];
        delete nextCmd[pumpId];
      } else {
        nextStatus[pumpId] = status;
        if (action) nextAction[pumpId] = action;
        if (commandId) nextCmd[pumpId] = commandId;
        else delete nextCmd[pumpId];
      }
      pumpStatusRef.current = nextStatus;
      pumpActionRef.current = nextAction;
      pumpCommandIdRef.current = nextCmd;
      setPumpStatus(nextStatus);
      setPumpAction(nextAction);
    },
    [],
  );

  // Poll the EXACT tracked pump command via the authenticated single-node
  // command read. GET-only. Never selects a random "latest relay command" and
  // never issues an automatic POST. A failed GET or a temporarily absent exact
  // command fails closed (keeps the intent locked / uncertain).
  const pollPumpCommands = useCallback(() => {
    if (!pumpsEnabled) return;
    if (pumpPollInFlightRef.current) return;
    const activePumps = Object.keys(pumpCommandIdRef.current).filter((pumpId) => {
      const status = pumpStatusRef.current[pumpId];
      return !!status && !isPumpTerminalStatus(status);
    });
    if (activePumps.length === 0) return;
    pumpPollInFlightRef.current = true;

    const reads = activePumps.map((pumpId) => {
      const nodeId = pumpNodeIdRef.current[pumpId];
      if (!nodeId) return Promise.resolve<{ pumpId: string; raw: Command[] | null }>({ pumpId, raw: null });
      return iotApi
        .getJson<Command[]>(`/iot/commands?node_id=${encodeURIComponent(nodeId)}&limit=100`)
        .then((raw) => ({ pumpId, raw }))
        .catch(() => ({ pumpId, raw: null }));
    });

    Promise.all(reads).then((results) => {
      pumpPollInFlightRef.current = false;
      if (!mountedRef.current) return;
      const nextStatus = { ...pumpStatusRef.current };
      const nextCmd = { ...pumpCommandIdRef.current };
      for (const result of results) {
        if (!result) continue;
        const { pumpId, raw } = result;
        const trackedId = pumpCommandIdRef.current[pumpId];
        if (!trackedId) continue;
        if (raw === null) {
          // Command-status GET failure → fail closed (uncertain), but keep the
          // exact command id so a subsequent successful GET may recover.
          nextStatus[pumpId] = 'UNCERTAIN';
          continue;
        }
        const list = (Array.isArray(raw) ? (raw as Command[]) : []).map((c) => ({
          ...c,
          id: c.command_id ?? c.id,
        }));
        const cmd = list.find((c) => c.id === trackedId);
        if (!cmd) {
          // Exact tracked command absent → uncertain (retain id, never unlock).
          nextStatus[pumpId] = 'UNCERTAIN';
          continue;
        }
        // Normalize the observed status — a missing/unknown/malformed status
        // is not terminal proof and must not re-enable the controls.
        const normalized = normalizePumpBackendStatus(cmd.status);
        if (isPumpTerminalStatus(normalized)) {
          nextStatus[pumpId] = normalized;
          delete nextCmd[pumpId]; // resolved — stop tracking
        } else {
          // REQUESTED / SENT_TO_TTN / UNCERTAIN → stay busy (retain id).
          nextStatus[pumpId] = normalized;
        }
      }
      pumpStatusRef.current = nextStatus;
      pumpCommandIdRef.current = nextCmd;
      setPumpStatus(nextStatus);
    });
  }, [pumpsEnabled]);

  // Ongoing pump command-status polling (~30s), never overlapping; GET-only.
  useEffect(() => {
    if (!IS_AWS_MODE) return;
    pollPumpCommands();
    const interval = setInterval(pollPumpCommands, 30000);
    return () => clearInterval(interval);
  }, [pollPumpCommands]);

  const openPumpConfirm = (binding: PumpBinding, action: 'start' | 'stop') => {
    if (!pumpsEnabled) return;
    const pc: PendingPumpConfirmation = {
      pumpId: binding.pumpId,
      nodeId: binding.nodeId,
      action,
      equipment: binding.nodeName,
      idempotencyKey: generatePumpIdempotencyKey(),
    };
    pendingPumpConfirmationRef.current = pc;
    setPendingPumpConfirmation(pc);
  };

  const cancelPumpConfirm = () => {
    pendingPumpConfirmationRef.current = null;
    setPendingPumpConfirmation(null);
  };

  // Issue exactly one semantic pump POST for a confirmed intent, carrying a
  // stable Idempotency-Key. Never auto-retries; never the generic relay endpoint.
  const confirmPumpCommand = async () => {
    if (!pumpsEnabled) return;
    const pc = pendingPumpConfirmationRef.current;
    if (!pc) return;
    // Synchronously consume the intent so a rapid double-click cannot dispatch twice.
    pendingPumpConfirmationRef.current = null;
    setPendingPumpConfirmation(null);

    const { pumpId, nodeId, action, idempotencyKey } = pc;

    // Submit-time fail-closed guards (role + not already busy), BEFORE the POST.
    if (!canIssuePhysicalCommands(user?.role)) {
      toast.error(t('farmer.errors.forbidden'));
      return;
    }
    if (isPumpBusyStatus(pumpStatusRef.current[pumpId])) {
      toast.error(t('farmer.pumps.conflict'));
      return;
    }

    pumpNodeIdRef.current = { ...pumpNodeIdRef.current, [pumpId]: nodeId };
    setPumpIntent(pumpId, 'REQUESTED', action);

    // FRESH pre-dispatch revalidation: the physically dispatched node MUST be
    // the exact node the farmer confirmed. A pump_id re-bound to another node,
    // a normalized collision, or decommissioning fails closed with ZERO POST.
    // Never trust the stale farmerNodes snapshot.
    try {
      const freshNodes = await iotApi.getJson<IoTNode[]>(nodeListPath);
      const canonicalPumpId = pumpId.trim();
      const canonicalMatches = (Array.isArray(freshNodes) ? freshNodes : []).filter((n) => {
        const pc = n?.pump_control;
        if (!pc || typeof pc !== 'object') return false;
        const rawId = pc.pump_id;
        return typeof rawId === 'string' && rawId.trim() === canonicalPumpId;
      });
      const bindingValid =
        canonicalMatches.length === 1 &&
        canonicalMatches[0].id === nodeId &&
        canonicalMatches[0].pump_control?.status === 'COMMISSIONED';
      if (!bindingValid) {
        setPumpIntent(pumpId, 'UNCERTAIN', action);
        toast.error(t('farmer.errors.uncertain'));
        return;
      }
    } catch {
      // Fresh node read failed → fail closed, no POST, no retry.
      setPumpIntent(pumpId, 'UNCERTAIN', action);
      toast.error(t('farmer.errors.uncertain'));
      return;
    }

    try {
      const res = await iotApi.post(
        `/iot/pumps/${encodeURIComponent(pumpId)}/command`,
        { action },
        idempotencyKey,
        { 'X-AgriSafe-Expected-Node-ID': nodeId },
      );
      // Exact command identity is mandatory: a non-empty command_id string is
      // required even for a terminal-looking response. Missing/null/empty/wrong
      // type → fail closed (UNCERTAIN), no success claim, no automatic retry.
      const commandId =
        typeof res?.command_id === 'string' && res.command_id.trim().length > 0
          ? res.command_id
          : undefined;

      if (!commandId) {
        setPumpIntent(pumpId, 'UNCERTAIN', action);
      } else {
        const status = normalizePumpBackendStatus(res?.status);
        if (isPumpTerminalStatus(status)) {
          setPumpIntent(pumpId, status, action);
        } else {
          // REQUESTED / SENT_TO_TTN / UNCERTAIN → track exact id and poll
          // (GET recovery resolves UNCERTAIN once the exact command is proven).
          setPumpIntent(pumpId, status, action, commandId);
          pollPumpCommands();
        }
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.semanticCode === 'conflict' || err.httpStatus === 409) {
          setPumpIntent(pumpId, 'UNCERTAIN', action);
          toast.error(t('farmer.pumps.conflict'));
        } else if (err.semanticCode === 'forbidden' || err.httpStatus === 403) {
          setPumpIntent(pumpId, null);
          toast.error(t('farmer.errors.forbidden'));
        } else {
          setPumpIntent(pumpId, 'UNCERTAIN', action);
          toast.error(t('farmer.errors.uncertain'));
        }
      } else {
        setPumpIntent(pumpId, 'UNCERTAIN', action);
        toast.error(t('farmer.errors.uncertain'));
      }
    }
  };

  // Demo mode: full control surface (unchanged behavior).
  useEffect(() => {
    if (IS_AWS_MODE) return;
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  if (IS_AWS_MODE) {
    const getStateLabel = (kind: StateKind, value: number): string => {
      if (kind === 'valve') {
        return value === 1 ? t('farmer.states.valveOpen') : t('farmer.states.valveClosed');
      }
      if (kind === 'pump') {
        return value === 1 ? t('farmer.states.pumpOn') : t('farmer.states.pumpOff');
      }
      return value === 1 ? t('farmer.states.motorOn') : t('farmer.states.motorOff');
    };

    const getFriendlyTypeLabel = (model: string | null | undefined): string => {
      switch (model) {
        case 'asvwf01':
        case 'valve_water_flow':
          return t('farmer.types.irrigationValve');
        case 'relay_4ch':
          return t('farmer.types.relayControl');
        case 'soil_air_ecth':
          return t('farmer.types.soilAirSensor');
        case 'pipe_pressure':
          return t('farmer.types.pressureSensor');
        default:
          return t('farmer.types.equipment');
      }
    };

    const actionLabel = (action: 'on' | 'off'): string =>
      action === 'on' ? t('controls.open') : t('controls.close');

    const commandStatusLabel = (
      kind: 'pending' | 'confirmed' | 'failed' | 'timeout' | 'uncertain' | null,
    ): string | null => {
      switch (kind) {
        case 'pending': return t('farmer.status.pending');
        case 'confirmed': return t('farmer.status.confirmed');
        case 'failed': return t('farmer.status.failed');
        case 'timeout': return t('farmer.status.timeout');
        case 'uncertain': return t('farmer.status.uncertain');
        default: return null;
      }
    };

    // Semantic pump command lifecycle → farmer-safe status language. A confirmed
    // relay command is NOT proof of physical motor state, so success/stop are
    // rendered as command confirmations, never "En marche"/"À l'arrêt".
    const pumpStatusLabel = (
      status: string | undefined,
      action: 'start' | 'stop' | undefined,
    ): string => {
      if (!status) return t('farmer.pumps.noRecentCommand');
      switch (status) {
        case 'REQUESTED':
        case 'SENT_TO_TTN':
          return action === 'start' ? t('farmer.pumps.startPending') : t('farmer.pumps.stopPending');
        case 'OBSERVED_SUCCESS':
          return action === 'start' ? t('farmer.pumps.startConfirmed') : t('farmer.pumps.stopConfirmed');
        case 'OBSERVED_FAILED':
        case 'TIMEOUT':
          return t('farmer.pumps.commandFailed');
        case 'UNCERTAIN':
          return t('farmer.status.uncertain');
        default:
          // Unknown status is not proof of "no command" — fail closed.
          return t('farmer.status.uncertain');
      }
    };

    const pumpBindings = pumpsEnabled ? pumpBindingsFor(farmerNodes) : [];
    // VANNES shows valve-capable nodes ONLY (by model identity), never passive
    // soil/pressure sensors or ordinary relays. A valve whose current state is
    // UNKNOWN stays visible (its state renders —, and no blind action is offered).
    const tableNodes = farmerNodes.filter((n) => VALVE_STATE_MODELS.has(n.model));
    const nodesById = new Map(farmerNodes.map((n) => [n.id, n]));

    const renderValveAction = (
      node: IoTNode,
      tg: { kind: 'scalar' | 'channel'; state: 0 | 1 | null; channelId?: string; channelN?: number },
      testIdPrefix = '',
    ) => {
      const testSuffix = tg.kind === 'scalar' ? '' : `-${tg.channelN}`;
      if (tg.state === null) {
        return (
          <span
            className="text-[var(--text-secondary)]"
            data-testid={`${testIdPrefix}valve-unknown-${node.id}${testSuffix}`}
          >
            {UNKNOWN}
          </span>
        );
      }
      const action: 'on' | 'off' = ACTION_BY_STATE[tg.state];
      const key =
        tg.kind === 'scalar'
          ? scalarTargetKey(node.id)
          : channelTargetKey(node.id, tg.channelId as string);
      const rawStatus = commandStatus[key];
      const statusKind = commandStatusKind(rawStatus);
      // Fail-closed: disable until the node's initial command read succeeds, and
      // while any backend/local non-terminal (or uncertain) safety state holds.
      const pending = commandsLoaded[node.id] !== true || isBusyStatus(rawStatus);
      const target: ValveTarget =
        tg.kind === 'scalar'
          ? { kind: 'scalar', nodeId: node.id }
          : { kind: 'channel', nodeId: node.id, channelId: tg.channelId as string };
      const channelLabel =
        tg.kind === 'channel' ? t('farmer.channelLabel', { n: tg.channelN }) : null;
      return (
        <div className="flex items-center gap-2" data-testid={`${testIdPrefix}valve-target-${node.id}${testSuffix}`}>
          {channelLabel && (
            <span
              className="text-xs text-[var(--text-secondary)]"
              data-testid={`${testIdPrefix}valve-channel-${node.id}${testSuffix}`}
            >
              {channelLabel}
            </span>
          )}
          <button
            onClick={() => openConfirm(target, action, node, channelLabel)}
            disabled={pending}
            className={`px-3 py-1 rounded-lg text-white text-xs ${
              action === 'on' ? 'bg-[var(--color-success)]' : 'bg-[var(--text-secondary)]'
            } hover:opacity-90 disabled:opacity-50`}
            data-testid={`${testIdPrefix}valve-action-${node.id}${testSuffix}`}
          >
            {actionLabel(action)}
          </button>
          {statusKind && (
            <span
              className="text-xs text-[var(--text-secondary)]"
              data-testid={`${testIdPrefix}valve-status-${node.id}${testSuffix}`}
            >
              {commandStatusLabel(statusKind)}
            </span>
          )}
        </div>
      );
    };

    const renderValveMobileCard = (node: IoTNode) => {
      const state = selectState(node);
      const stateText = state === null ? UNKNOWN : getStateLabel(state.kind, state.value);
      const lastCommMs = nodeTimestampMs(node);
      const lastCommText = lastCommMs === null ? UNKNOWN : formatDateTime(lastCommMs);
      const targets = valveTargetsFor(node);
      return (
        <div key={node.id} className="card" data-testid={`pilotage-mobile-card-${node.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-medium text-[var(--text-primary)] truncate" data-testid={`pilotage-mobile-name-${node.id}`}>
                {displayText(node.name)}
              </h3>
              <p className="text-xs text-[var(--text-secondary)]" data-testid={`pilotage-mobile-sector-${node.id}`}>
                {displayText(node.sector_name)}
              </p>
            </div>
            <span className="text-sm font-medium text-[var(--text-primary)] shrink-0" data-testid={`pilotage-mobile-state-${node.id}`}>
              {stateText}
            </span>
          </div>
          <p className="text-xs text-[var(--text-tertiary)] mt-2" data-testid={`pilotage-mobile-last-comm-${node.id}`}>
            {t('farmer.columns.lastCommunication')}: {lastCommText}
          </p>
          <div className="mt-3">
            {!canControl || targets.length === 0 ? (
              <span className="text-[var(--text-secondary)]">{UNKNOWN}</span>
            ) : (
              <div className="flex flex-wrap gap-2">
                {targets.map((tg) => renderValveAction(node, tg, 'mobile-'))}
              </div>
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-6" data-testid="farmer-pilotage">
        <div>
          <h1 className="text-heading-2 text-[var(--text-primary)]" data-testid="pilotage-heading">
            {t('farmer.title')}
          </h1>
          <p className="text-body text-[var(--text-secondary)] mt-1" data-testid="pilotage-subtitle">
            {t('farmer.subtitle')}
          </p>
        </div>

        {loadError && (
          <div
            className="card border border-[var(--color-danger)] text-[var(--color-danger)]"
            data-testid="pilotage-error"
          >
            {t('farmer.loadError')}
          </div>
        )}

        {/* POMPES — semantic pump controls (PR #105), farmer-safe cards. */}
        {pumpBindings.length > 0 && (
          <section data-testid="pump-section">
            <h2 className="text-heading-4 text-[var(--text-primary)] mb-3" data-testid="pumps-heading">
              {t('farmer.pumps.title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pumpBindings.map((binding) => {
                const status = pumpStatus[binding.pumpId];
                const action = pumpAction[binding.pumpId];
                const busy = isPumpBusyStatus(status);
                const actionable = canControl && binding.commissioned && !busy;
                const node = nodesById.get(binding.nodeId);
                const lastCommMs = node ? nodeTimestampMs(node) : null;
                return (
                  <div key={binding.pumpId} className="card" data-testid={`pump-card-${binding.pumpId}`}>
                    <h3 className="text-heading-4 text-[var(--text-primary)] mb-3" data-testid={`pump-name-${binding.pumpId}`}>
                      {binding.nodeName === UNKNOWN ? t('farmer.pumps.genericName') : binding.nodeName}
                    </h3>
                    <dl className="space-y-2 text-sm">
                      <div className="flex items-baseline justify-between gap-2">
                        <dt className="text-[var(--text-secondary)]">{t('farmer.pumps.remoteCommand')}</dt>
                        <dd className="font-medium text-[var(--text-primary)]" data-testid={`pump-availability-${binding.pumpId}`}>
                          {binding.commissioned ? t('farmer.pumps.available') : t('farmer.pumps.unavailable')}
                        </dd>
                      </div>
                      <div className="flex items-baseline justify-between gap-2">
                        <dt className="text-[var(--text-secondary)]">{t('farmer.pumps.lastCommand')}</dt>
                        <dd className="font-medium text-[var(--text-primary)]" data-testid={`pump-status-${binding.pumpId}`}>
                          {pumpStatusLabel(status, action)}
                        </dd>
                      </div>
                      <div className="flex items-baseline justify-between gap-2">
                        <dt className="text-[var(--text-secondary)]">{t('farmer.pumps.lastCommunication')}</dt>
                        <dd className="font-medium text-[var(--text-primary)]" data-testid={`pump-last-comm-${binding.pumpId}`}>
                          {lastCommMs !== null ? formatDateTime(lastCommMs) : UNKNOWN}
                        </dd>
                      </div>
                    </dl>
                    {!binding.commissioned && (
                      <p className="text-xs text-[var(--text-secondary)] mt-2" data-testid={`pump-unavailable-${binding.pumpId}`}>
                        {t('farmer.pumps.remoteUnavailable')}
                      </p>
                    )}
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => openPumpConfirm(binding, 'start')}
                        disabled={!actionable}
                        className="flex-1 min-w-[110px] px-3 py-2 bg-[var(--color-success)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                        data-testid={`pump-start-${binding.pumpId}`}
                      >
                        {t('farmer.pumps.start')}
                      </button>
                      <button
                        onClick={() => openPumpConfirm(binding, 'stop')}
                        disabled={!actionable}
                        className="flex-1 min-w-[110px] px-3 py-2 bg-[var(--color-danger)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                        data-testid={`pump-stop-${binding.pumpId}`}
                      >
                        {t('farmer.pumps.stop')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* VANNES */}
        <h2 className="text-heading-4 text-[var(--text-primary)] mb-3" data-testid="valves-heading">
          {t('farmer.valvesTitle')}
        </h2>

        {/* Desktop/tablet valve table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm" data-testid="pilotage-table">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left text-[var(--text-secondary)]">{t('farmer.columns.name')}</th>
                <th className="px-3 py-2 text-left text-[var(--text-secondary)]">{t('farmer.columns.type')}</th>
                <th className="px-3 py-2 text-left text-[var(--text-secondary)]">{t('farmer.columns.sector')}</th>
                <th className="px-3 py-2 text-left text-[var(--text-secondary)]">{t('farmer.columns.state')}</th>
                <th className="px-3 py-2 text-left text-[var(--text-secondary)]">{t('farmer.columns.lastCommunication')}</th>
                <th className="px-3 py-2 text-left text-[var(--text-secondary)]">{t('farmer.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {tableNodes.map((node, index) => {
                const state = selectState(node);
                const stateText = state === null ? UNKNOWN : getStateLabel(state.kind, state.value);
                const lastCommMs = nodeTimestampMs(node);
                const lastCommText = lastCommMs === null ? UNKNOWN : formatDateTime(lastCommMs);
                const targets = valveTargetsFor(node);

                return (
                  <tr key={index} className="border-t border-[var(--border-light)]" data-testid={`pilotage-row-${index}`}>
                    <td className="px-3 py-2 text-[var(--text-primary)]" data-testid={`pilotage-name-${index}`}>
                      {displayText(node.name)}
                    </td>
                    <td className="px-3 py-2 text-[var(--text-primary)]" data-testid={`pilotage-type-${index}`}>
                      {getFriendlyTypeLabel(node.model)}
                    </td>
                    <td className="px-3 py-2 text-[var(--text-primary)]" data-testid={`pilotage-sector-${index}`}>
                      {displayText(node.sector_name)}
                    </td>
                    <td className="px-3 py-2 text-[var(--text-primary)]" data-testid={`pilotage-state-${index}`}>
                      {stateText}
                    </td>
                    <td className="px-3 py-2 text-[var(--text-primary)]" data-testid={`pilotage-last-comm-${index}`}>
                      {lastCommText}
                    </td>
                    <td className="px-3 py-2" data-testid={`pilotage-actions-${index}`}>
                      {!canControl || targets.length === 0 ? (
                        <span className="text-[var(--text-secondary)]">{UNKNOWN}</span>
                      ) : (
                        <div className="flex flex-col gap-1 items-start">
                          {targets.map((tg) => renderValveAction(node, tg))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile valve cards (farmer-friendly, no horizontal table scroll) */}
        <div className="md:hidden space-y-3" data-testid="pilotage-mobile-list">
          {tableNodes.map((node) => renderValveMobileCard(node))}
        </div>

        {!loadError && loaded && farmerNodes.length === 0 && (
          <div className="text-center py-12" data-testid="pilotage-empty">
            <p className="text-body text-[var(--text-secondary)]">{t('farmer.noEquipment')}</p>
          </div>
        )}

        {pendingConfirmation && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            data-testid="valve-confirm-dialog"
            role="dialog"
            aria-modal="true"
          >
            <div className="card w-full max-w-md p-5 bg-[var(--surface)]">
              <h2 className="text-heading-4 text-[var(--text-primary)] mb-3" data-testid="valve-confirm-title">
                {t('farmer.confirm.title')}
              </h2>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-[var(--text-secondary)]">{t('farmer.confirm.action')}</dt>
                  <dd className="text-[var(--text-primary)] font-medium">{actionLabel(pendingConfirmation.action)}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-[var(--text-secondary)]">{t('farmer.confirm.equipment')}</dt>
                  <dd className="text-[var(--text-primary)] font-medium">{pendingConfirmation.equipment}</dd>
                </div>
                {pendingConfirmation.sector && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-[var(--text-secondary)]">{t('farmer.confirm.sector')}</dt>
                    <dd className="text-[var(--text-primary)] font-medium">{pendingConfirmation.sector}</dd>
                  </div>
                )}
                {pendingConfirmation.channelLabel && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-[var(--text-secondary)]">{t('farmer.confirm.channel')}</dt>
                    <dd className="text-[var(--text-primary)] font-medium">{pendingConfirmation.channelLabel}</dd>
                  </div>
                )}
              </dl>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={cancelConfirm}
                  className="px-3 py-2 text-sm border border-[var(--border-medium)] rounded-lg text-[var(--text-primary)] hover:bg-[var(--gray-50)]"
                  data-testid="valve-confirm-cancel"
                >
                  {t('farmer.confirm.cancel')}
                </button>
                <button
                  onClick={confirmCommand}
                  disabled={
                    !canControl ||
                    commandsLoaded[pendingConfirmation.target.nodeId] !== true ||
                    isBusyStatus(commandStatus[targetKey(pendingConfirmation.target)])
                  }
                  className="px-3 py-2 text-sm bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                  data-testid="valve-confirm-proceed"
                >
                  {t('farmer.confirm.proceed')}
                </button>
              </div>
            </div>
          </div>
        )}

        {pendingPumpConfirmation && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            data-testid="pump-confirm-dialog"
            role="dialog"
            aria-modal="true"
          >
            <div className="card w-full max-w-md p-5 bg-[var(--surface)]">
              <h2 className="text-heading-4 text-[var(--text-primary)] mb-3" data-testid="pump-confirm-title">
                {pendingPumpConfirmation.action === 'start'
                  ? t('farmer.pumps.confirmStartTitle')
                  : t('farmer.pumps.confirmStopTitle')}
              </h2>
              <p className="text-sm text-[var(--text-primary)] mb-4" data-testid="pump-confirm-equipment">
                {pendingPumpConfirmation.equipment}
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={cancelPumpConfirm}
                  className="px-3 py-2 text-sm border border-[var(--border-medium)] rounded-lg text-[var(--text-primary)] hover:bg-[var(--gray-50)]"
                  data-testid="pump-confirm-cancel"
                >
                  {t('farmer.pumps.cancel')}
                </button>
                <button
                  onClick={confirmPumpCommand}
                  className="px-3 py-2 text-sm bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                  data-testid="pump-confirm-proceed"
                >
                  {t('farmer.pumps.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const controlValve = async (node: IoTNode, state: 'on' | 'off') => {
    setBusy(node.id);
    try {
      await iotApi.post(`/iot/nodes/${node.id}/valve`, { state });
      toast.success(t('controls.commandSent', {
        action: t(state === 'on' ? 'controls.open' : 'controls.close'),
        node: node.name
      }));
      setTimeout(refresh, 800);
    } catch (e) {
      console.error('Valve control failed:', e);
      toast.error(t('errors.commandFailed'));
    } finally {
      setBusy(null);
    }
  };

  const simulate = async (node: IoTNode, valve: number) => {
    try {
      await iotApi.post(`/ttn/simulate/${node.id}?valve=${valve}`);
      toast.success(t('controls.simulateSuccess', { node: node.name }));
      setTimeout(refresh, 500);
    } catch (e) {
      console.error('Simulate reading failed:', e);
      toast.error(t('errors.simulateFailed'));
    }
  };

  const ackAlert = async (id: string) => {
    try {
      await iotApi.post(`/iot/alerts/${id}/ack`);
      refresh();
    } catch (e) {
      console.error('Alert ack failed:', e);
      toast.error(t('errors.ackFailed'));
    }
  };

  const exportReport = async (
    scope: 'node' | 'sector',
    id: string,
    fmt: 'csv' | 'pdf',
    label: string,
  ) => {
    try {
      const path =
        scope === 'node'
          ? `/iot/nodes/${id}/report?hours=${period}&format=${fmt}`
          : `/iot/sectors/${id}/report?hours=${period}&format=${fmt}`;
      await iotApi.download(path, `bilan-${scope}.${fmt}`);
      toast.success(t('export.success', { format: fmt.toUpperCase(), label }));
    } catch (e) {
      console.error('Export failed:', e);
      toast.error(t('errors.exportFailed'));
    }
  };

  const statCards = [
    { label: t('stats.nodes'), value: stats?.nodes_total ?? 0, color: 'var(--color-primary)', icon: Cpu },
    { label: t('stats.valvesOpen'), value: stats?.valves_open ?? 0, color: 'var(--color-success)', icon: Droplets },
    { label: t('stats.activeAlerts'), value: stats?.open_alerts ?? 0, color: 'var(--color-warning)', icon: AlertTriangle },
    { label: t('stats.commandsConfirmed'), value: stats?.commands_success ?? 0, color: 'var(--color-info)', icon: CheckCircle2 },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-heading-2 text-[var(--text-primary)]">{t('title')}</h1>
          <p className="text-body text-[var(--text-secondary)] mt-1">
            {t('subtitle')}
          </p>
        </div>
        <button
          onClick={refresh}
          className="flex items-center gap-2 px-4 py-2 border border-[var(--border-medium)] rounded-lg hover:bg-[var(--gray-50)]"
          data-testid="iot-refresh"
        >
          <RefreshCw className="w-4 h-4" /> {t('refresh')}
        </button>
      </div>

      {!TELEMETRY_ENABLED && (
        <div className="card border border-[var(--color-info)] bg-blue-50 text-blue-800">
          {t('telemDisabled')}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <div key={i} className="card text-center" data-testid={`iot-stat-${i}`}>
            <s.icon className="w-6 h-6 mx-auto mb-2" style={{ color: s.color }} />
            <div className="text-2xl font-bold mb-1" style={{ color: s.color }}>{s.value}</div>
            <div className="text-sm text-[var(--text-secondary)]">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Alerts */}
      {alerts.filter((a) => a.status === 'open').length > 0 && (
        <div className="space-y-2">
          {alerts.filter((a) => a.status === 'open').map((a) => (
            <div key={a.id} className="flex items-center justify-between p-3 rounded-lg bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/30" data-testid={`iot-alert-${a.id}`}>
              <div className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                <AlertTriangle className="w-4 h-4 text-[var(--color-warning)]" />
                {a.message}
              </div>
              <button onClick={() => ackAlert(a.id)} className="text-xs px-3 py-1 rounded-md border border-[var(--border-medium)] hover:bg-[var(--gray-50)]" data-testid={`iot-alert-ack-${a.id}`}>
                {t('acknowledge')}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Nodes */}
      {loading ? (
        <div className="text-center py-12 text-[var(--text-secondary)]">{t('loading')}</div>
      ) : nodes.length === 0 ? (
        <div className="text-center py-12" data-testid="iot-empty">
          <Radio className="w-12 h-12 text-[var(--text-secondary)] mx-auto mb-4" />
          <p className="text-body text-[var(--text-secondary)]">{t('empty')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {nodes.map((node) => {
            const m = node.latest?.measures || {};
            const valveOpen = (node.valve_state ?? m.valve_state) === 1;
            const bat = node.battery_v ?? m.battery_v;
            const valveCapable = supportsValveControl(node.model);
            return (
              <div key={node.id} className="card" data-testid={`iot-node-${node.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-[var(--color-primary)]/10">
                      <Radio className="w-5 h-5 text-[var(--color-primary)]" />
                    </div>
                    <div>
                      <h3 className="text-heading-4 text-[var(--text-primary)]">{node.name}</h3>
                      <p className="text-xs text-[var(--text-tertiary)] font-mono">DevEUI {node.dev_eui}</p>
                      <p className="text-xs text-[var(--text-tertiary)]">{node.model}</p>
                    </div>
                  </div>
                  {valveCapable && <span
                    className="px-2 py-1 text-xs rounded-full text-white"
                    style={{ backgroundColor: valveOpen ? 'var(--color-success)' : 'var(--text-secondary)' }}
                    data-testid={`iot-valve-state-${node.id}`}
                  >
                    {t('valveLabel')} {valveOpen ? t('valveState.open') : t('valveState.closed')}
                  </span>}
                </div>

                {/* Live metrics */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="text-center p-3 bg-[var(--gray-50)] rounded-lg">
                    <Battery className="w-5 h-5 mx-auto mb-1 text-[var(--color-info)]" />
                    <div className="text-sm font-semibold text-[var(--text-primary)]">{bat != null ? `${bat} V` : '—'}</div>
                    <div className="text-xs text-[var(--text-secondary)]">{t('battery')}</div>
                  </div>
                  <div className="text-center p-3 bg-[var(--gray-50)] rounded-lg">
                    <Waves className="w-5 h-5 mx-auto mb-1 text-[var(--color-primary)]" />
                    <div className="text-sm font-semibold text-[var(--text-primary)]">{m.flow_velocity_lpm != null ? `${m.flow_velocity_lpm} L/min` : '—'}</div>
                    <div className="text-xs text-[var(--text-secondary)]">{t('flow')}</div>
                  </div>
                  <div className="text-center p-3 bg-[var(--gray-50)] rounded-lg">
                    <Activity className="w-5 h-5 mx-auto mb-1 text-[var(--color-accent)]" />
                    <div className="text-sm font-semibold text-[var(--text-primary)]">{m.reporting_interval_s != null ? `${m.reporting_interval_s}s` : '—'}</div>
                    <div className="text-xs text-[var(--text-secondary)]">{t('interval')}</div>
                  </div>
                </div>
                <p className="text-xs text-[var(--text-tertiary)] mb-4">{t('lastReading')} {fmtTime(node.latest?.ts)}</p>

                {/* 24h telemetry chart */}
                {TELEMETRY_ENABLED && <div className="mb-4 pt-2 border-t border-[var(--border-light)]">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-[var(--text-secondary)]">{t('telemetry')}</p>
                    <div className="flex items-center gap-1" data-testid={`period-selector-${node.id}`}>
                      {PERIODS.map((p) => (
                        <button
                          key={p.key}
                          onClick={() => setPeriod(p.key)}
                          className={`px-2 py-0.5 text-xs rounded-md border transition-colors ${
                            period === p.key
                              ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]'
                              : 'bg-[var(--surface)] text-[var(--text-secondary)] border-[var(--border-light)] hover:bg-[var(--gray-50)]'
                          }`}
                          data-testid={`period-${p.key}-${node.id}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <TelemetryChart data={telemetry[node.id] || []} />

                  {/* Aggregated summary */}
                  {summaries[node.id] && (
                    <div className="grid grid-cols-3 gap-2 mt-3" data-testid={`telemetry-summary-${node.id}`}>
                      {[
                        { label: t('summary.totalVolume'), value: `${summaries[node.id].total_volume_l} L`, color: 'var(--color-info)' },
                        { label: t('summary.avgFlow'), value: `${summaries[node.id].avg_flow_lpm} L/min`, color: 'var(--color-primary)' },
                        { label: t('summary.maxFlow'), value: `${summaries[node.id].max_flow_lpm} L/min`, color: 'var(--color-primary)' },
                        { label: t('summary.valveOpen'), value: fmtMinutes(summaries[node.id].valve_open_minutes), color: 'var(--color-success)' },
                        { label: t('summary.batteryMin'), value: summaries[node.id].battery_min_v != null ? `${summaries[node.id].battery_min_v} V` : '—', color: 'var(--text-primary)' },
                        { label: t('summary.batteryMax'), value: summaries[node.id].battery_max_v != null ? `${summaries[node.id].battery_max_v} V` : '—', color: 'var(--text-primary)' },
                      ].map((s, i) => (
                        <div key={i} className="bg-[var(--gray-50)] rounded-lg p-2 text-center">
                          <div className="text-sm font-semibold" style={{ color: s.color }}>{s.value}</div>
                          <div className="text-[10px] text-[var(--text-secondary)]">{s.label}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Export water balance */}
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <span className="text-xs text-[var(--text-secondary)]">{t('export.waterBalance')}</span>
                    <button
                      onClick={() => exportReport('node', node.id, 'csv', node.name)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-[var(--border-medium)] hover:bg-[var(--gray-50)]"
                      data-testid={`export-node-csv-${node.id}`}
                    >
                      <FileDown className="w-3.5 h-3.5" /> CSV
                    </button>
                    <button
                      onClick={() => exportReport('node', node.id, 'pdf', node.name)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-[var(--border-medium)] hover:bg-[var(--gray-50)]"
                      data-testid={`export-node-pdf-${node.id}`}
                    >
                      <FileText className="w-3.5 h-3.5" /> PDF
                    </button>
                    {node.sector_id && (
                      <button
                        onClick={() => exportReport('sector', node.sector_id as string, 'pdf', node.sector_name || 'secteur')}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-dashed border-[var(--border-medium)] text-[var(--text-secondary)] hover:bg-[var(--gray-50)]"
                        data-testid={`export-sector-pdf-${node.id}`}
                        title={t('export.sectorTitle', { sector: node.sector_name || '' })}
                      >
                        <FileText className="w-3.5 h-3.5" /> {t('export.sectorPdf')}
                      </button>
                    )}
                  </div>
                </div>}

                {/* Valve control */}
                {valveCapable && <div className="flex flex-wrap gap-2 pt-4 border-t border-[var(--border-light)]">
                  <button
                    onClick={() => controlValve(node, 'on')}
                    disabled={busy === node.id}
                    className="flex-1 min-w-[110px] flex items-center justify-center gap-2 px-3 py-2 bg-[var(--color-success)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                    data-testid={`iot-valve-on-${node.id}`}
                  >
                    <Power className="w-4 h-4" /> {t('controls.open')}
                  </button>
                  <button
                    onClick={() => controlValve(node, 'off')}
                    disabled={busy === node.id}
                    className="flex-1 min-w-[110px] flex items-center justify-center gap-2 px-3 py-2 bg-[var(--color-danger)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                    data-testid={`iot-valve-off-${node.id}`}
                  >
                    <Power className="w-4 h-4" /> {t('controls.close')}
                  </button>
                  {TTN_SIMULATOR_ENABLED && <button
                    onClick={() => simulate(node, valveOpen ? 0 : 1)}
                    className="px-3 py-2 border border-dashed border-[var(--border-medium)] rounded-lg text-xs text-[var(--text-secondary)] hover:bg-[var(--gray-50)]"
                    title={t('controls.simulateTitle')}
                    data-testid={`iot-simulate-${node.id}`}
                  >
                    {t('controls.simulate')}
                  </button>}
                </div>}
              </div>
            );
          })}
        </div>
      )}

      {/* Command ledger */}
      <div>
        <h2 className="text-heading-4 text-[var(--text-primary)] mb-3">{t('commandHistory')}</h2>
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm" data-testid="iot-command-table">
            <thead>
              <tr className="border-b border-[var(--border-light)] text-left text-[var(--text-secondary)]">
                <th className="px-4 py-3 font-medium">{t('commandTable.type')}</th>
                <th className="px-4 py-3 font-medium">{t('commandTable.params')}</th>
                <th className="px-4 py-3 font-medium">{t('commandTable.fport')}</th>
                <th className="px-4 py-3 font-medium">{t('commandTable.payload')}</th>
                <th className="px-4 py-3 font-medium">{t('commandTable.requested')}</th>
                <th className="px-4 py-3 font-medium">{t('commandTable.status')}</th>
              </tr>
            </thead>
            <tbody>
              {commands.slice(0, 15).map((c) => {
                const st = statusStyle(c.status);
                return (
                  <tr key={c.id} className="border-b border-[var(--border-light)] last:border-0" data-testid={`iot-command-${c.id}`}>
                    <td className="px-4 py-3 text-[var(--text-primary)]">{c.type}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{JSON.stringify(c.params)}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{c.f_port}</td>
                    <td className="px-4 py-3 font-mono text-[var(--text-secondary)]">0x{c.payload_hex}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{fmtTime(c.requested_at)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: st.color }}>
                        <st.icon className="w-4 h-4" /> {st.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {commands.length === 0 && (
            <div className="text-center py-8 text-[var(--text-secondary)]">{t('noCommands')}</div>
          )}
        </div>
      </div>

      {/* Automated report schedules */}
      <ReportSchedules />
    </div>
  );
};

export default IoTNodes;
